import { map, max } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AppSectionSettings } from './settings-components/AppSection'
import { Checkin } from './settings-components/Checkin'
import { CryptoModule } from './settings-components/CryptoModule'
import { Editions } from './settings-components/Editions'
import { FestivalDates } from './settings-components/FestivalDates'
import { FooterManagement } from './settings-components/FooterManagement'
import { FriendsManagement } from './settings-components/FriendsManagement'
import { Genders } from './settings-components/Genders'
import { GeneralSettings } from './settings-components/GeneralSettings'
import { HeroSettingsComp } from './settings-components/HeroSettings'
import { ModuleManagement } from './settings-components/ModuleManagement'
import { PaymentMethods } from './settings-components/PaymenthMethods'
import { SocialLinks } from './settings-components/SocialLinks'
import { TicketsComp } from './settings-components/Tickets'
import { TopupModule } from './settings-components/Topup'
import Loader from '../../../common/components/Loader'
import { Days, Languages } from '../../../common/models'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getFestivalDays } from '../../../core/services/dates-settings'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Card, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { GooglePay } from './settings-components/GooglePay'
import { ApplePay } from './settings-components/ApplePay'

export const SettingsGeneralPage: React.FC = () => {
  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name
  const editionId = storeFestivals
    .find((f) => f.platformName === festivalName)
    ?.editions.find((e) => e.active)?.id
  const [loading, setLoading] = useState<boolean>(false)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [days, setDays] = useState<Days[]>([])
  const [endTime, setEndTime] = useState<number>(0)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        setAllLanguages(allLanguagesResponse.data)
        if (editionId) {
          const resp = await getFestivalDays(editionId)
          setDays(resp.data)
          const time = map(resp.data, d => d.endAt)
          setEndTime(max(time) || 0)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  return (
    <>
      <Header pageTitle="Settings" pageSubtitle="General" />
      {loading && <Loader />}
      <div className="max-h-[calc(100vh-115px)] overflow-y-auto space-y-5">
        <GeneralSettings
          hasEditPermission={hasEditPermission}
          allLanguages={allLanguages}
        />
        <Genders
          hasEditPermission={hasEditPermission}
          allLanguages={allLanguages}
        />
        <Editions hasEditPermission={hasEditPermission} />

        <AppSectionSettings hasEditPermission={hasEditPermission} />
        <TicketsComp hasEditPermission={hasEditPermission} />
        <Checkin
          hasEditPermission={hasEditPermission}
          allLanguages={allLanguages}
        />
        <TopupModule hasEditPermission={hasEditPermission} endTime={endTime} />
        <ModuleManagement hasEditPermission={hasEditPermission} />
        <CryptoModule hasEditPermission={hasEditPermission} />
        <GooglePay hasEditPermission={hasEditPermission} />
        <ApplePay hasEditPermission={hasEditPermission} />

        <PaymentMethods hasEditPermission={hasEditPermission} />

        <FestivalDates
          days={days}
          allLanguages={allLanguages}
          setDays={setDays}
          hasEditPermission={hasEditPermission}
        />

        <HeroSettingsComp
          hasEditPermission={hasEditPermission}
          allLanguages={allLanguages}
        />
        <FooterManagement
          hasEditPermission={hasEditPermission}
          allLanguages={allLanguages}
        />

        <SocialLinks hasEditPermission={hasEditPermission} />
        <FriendsManagement hasEditPermission={hasEditPermission} />

        <HeroSettingsComp
          hasEditPermission={hasEditPermission}
          allLanguages={allLanguages}
        />
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
